// apps/nos/client/src/services/uploads_api.ts
import axios from "axios";
import { useAuth } from "@clerk/react";
import { serverURL } from "@/utils/serverURL";
import type {
  CompleteResponse,
  FinalizeResponse,
  PresignResponse,
} from "@/utils/types/upload";

type Headers = Record<string, string>;

export type PresignArgs = {
  draft_id: string;
  kind: "chromatogram";
  filename: string;
  content_type: string;
  size_bytes: number;
  metadata?: { machine: "agilent" | "thermo" };
};

export async function presignUpload(
  args: PresignArgs,
  headers: Headers,
  signal?: AbortSignal,
): Promise<PresignResponse> {
  const url = serverURL`/me/flavor/uploads/presign`;
  const { data } = await axios.post<PresignResponse>(url, args, { headers, signal });
  return data;
}

export async function completeUpload(
  fileUploadId: string,
  headers: Headers,
  signal?: AbortSignal,
): Promise<CompleteResponse> {
  const url = serverURL`/me/flavor/uploads/${fileUploadId}/complete`;
  const { data } = await axios.post<CompleteResponse>(url, {}, { headers, signal });
  return data;
}

export async function patchUpload(
  fileUploadId: string,
  patch: { metadata: { machine?: "agilent" | "thermo" } },
  headers: Headers,
): Promise<void> {
  const url = serverURL`/me/flavor/uploads/${fileUploadId}`;
  await axios.patch(url, patch, { headers });
}

// Reliable soft-delete. Retries with backoff and uses fetch `keepalive` so the
// request survives a tab close / navigation right after the user removes a file
// (XHR/axios can't keepalive). Terminal on 2xx (soft-deleted), 404 (already gone
// / not owned by this profile), and 409 (already finalized). Throws only after
// exhausting retries on transient failures — callers surface that to monitoring;
// finalize reconciles any straggler server-side, so a lost delete isn't fatal.
export async function deleteUpload(
  fileUploadId: string,
  headers: Headers,
  opts?: { retries?: number },
): Promise<void> {
  const url = serverURL`/me/flavor/uploads/${fileUploadId}`;
  const retries = Math.max(1, opts?.retries ?? 3);
  let lastErr: unknown;
  for (let attempt = 0; attempt < retries; attempt++) {
    let res: Response | null = null;
    try {
      res = await fetch(url, { method: "DELETE", headers, keepalive: true });
    } catch (err) {
      lastErr = err; // network error → retryable
    }
    if (res) {
      if (res.ok || res.status === 404) return; // success / already gone
      if (res.status === 409) {
        throw new Error("delete failed: 409 already finalized"); // terminal
      }
      lastErr = new Error(`delete failed: ${res.status}`); // 5xx etc → retryable
    }
    if (attempt < retries - 1) {
      await new Promise((r) => setTimeout(r, 200 * 2 ** attempt));
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error("delete failed");
}

// One authoritative draft upload row (server-confirmed 'uploaded'), scoped to
// the requesting profile by the server. Used to reconcile the local chroma set
// on draft rehydrate.
export type DraftUpload = {
  file_upload_id: string;
  original_filename: string;
  content_type: string;
  size_bytes: number;
  metadata: Record<string, unknown>;
  status: "uploaded";
  uploaded_at: string | null;
  created_at: string;
};

export async function listDraftUploads(
  draftId: string,
  headers: Headers,
  signal?: AbortSignal,
): Promise<DraftUpload[]> {
  const url = serverURL`/me/flavor/uploads`;
  const { data } = await axios.get<{ uploads: DraftUpload[] }>(url, {
    headers,
    params: { draft_id: draftId, kind: "chromatogram" },
    signal,
  });
  return data.uploads;
}

export async function finalizeUploads(
  args: {
    draft_id: string;
    name: string;
    kind: "chromatogram";
    // Per-file experiment-config assignment. Server matches each entry against
    // the finalized rows, drops unknowns, and inserts platform.chromatogram rows
    // idempotently. Omit when no configs are assigned (worker uses a default).
    configs?: { file_upload_id: string; user_exp_config_id: string }[];
  },
  headers: Headers,
): Promise<FinalizeResponse> {
  const url = serverURL`/me/flavor/uploads/finalize`;
  const { data } = await axios.post<FinalizeResponse>(url, args, { headers });
  return data;
}

// XHR-based PUT to S3 with progress events. fetch() can't expose request-body
// progress; XHR can.
export type PutToS3Args = {
  url: string;
  file: File;
  requiredHeaders: Record<string, string>;
  onProgress?: (pct: number) => void;
  signal?: AbortSignal;
};

export function putFileToS3(args: PutToS3Args): Promise<{ etag: string | null }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", args.url, true);
    for (const [k, v] of Object.entries(args.requiredHeaders)) {
      xhr.setRequestHeader(k, v);
    }

    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable && args.onProgress) {
        args.onProgress(Math.round((ev.loaded / ev.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ etag: xhr.getResponseHeader("ETag") });
      } else {
        reject(new Error(`S3 PUT failed: ${xhr.status} ${xhr.statusText}`));
      }
    };
    xhr.onerror = () => reject(new Error("S3 PUT network error"));
    xhr.onabort = () => reject(new Error("S3 PUT aborted"));

    if (args.signal) {
      args.signal.addEventListener("abort", () => xhr.abort());
    }

    xhr.send(args.file);
  });
}

// Convenience hook to get auth headers (Clerk JWT).
export function useAuthHeaders(): () => Promise<Headers> {
  const { getToken } = useAuth();
  return async () => {
    const token = await getToken();
    if (!token) throw new Error("not authenticated");
    return { Authorization: `Bearer ${token}` };
  };
}
